import React from 'react';
import SEO from '../components/SEO';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedin, faTwitter, faGithub } from '@fortawesome/free-brands-svg-icons';
import BackgroundDesign from '../components/BackgroundDesign';
import styled, { keyframes } from 'styled-components';

const AdditionalImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 2rem;

  @media (max-width: 768px) {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.5rem;
    padding: 0;
    margin: 1rem 0;
    width: 100%;
  }
`;

const AdditionalImage = styled.div`
  position: relative;
  height: 200px;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.3s ease;

  @media (max-width: 768px) {
    height: 150px;
    border-radius: 8px;
  }

  &:hover {
    transform: translateY(-5px);

    .image-caption {
      opacity: 1;
    }
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .image-caption {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 1rem;
    background: rgba(0, 0, 0, 0.7);
    color: white;
    font-size: 0.9rem;
    opacity: 0;
    transition: opacity 0.3s ease;

    @media (max-width: 768px) {
      opacity: 1;
      font-size: 0.7rem;
      padding: 0.3rem;
    }
  }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const MainContent = styled.main`
  flex: 1;
  width: 100%;
  padding: 2rem 0;
  margin-top: 80px;
  background: ${props => props.theme.colors.background};
  
  @media (max-width: 768px) {
    padding: 1rem 0;
    margin-top: 60px;
  }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const PageHero = styled.section`
  position: relative;
  height: 100vh;
  width: 100%;
  display: flex ;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
     background: linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.1)),
              url(/images/photo_5802988364859820042_y.jpg) center/cover no-repeat;
    
    filter: brightness(0.3);
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 150px;
    background: linear-gradient(to top, ${props => props.theme.colors.background}, transparent);
    z-index: 2;
  }
`;

const PageHeroContent = styled.div`
  position: relative;
  z-index: 3;
  text-align: center;
  color: white;
  max-width: 800px;
  padding: 0 1rem;
  animation: ${fadeUp} 1s ease-out;
  
  @media (max-width: 768px) {
    max-width: 100%;
    padding: 0 1rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 4rem;
  font-weight: 800;
  margin-bottom: 1.5rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  
  span {
    color: ${props => props.theme.colors.primary};
  }
  
  @media (max-width: 768px) {
    font-size: 2.5rem;
    margin-bottom: 1rem;
    letter-spacing: 1px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 1.2rem;
  line-height: 1.8;
  opacity: 0.9;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
    margin-bottom: 1.5rem;
  }
`;


const TeamSection = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 4rem 2rem;
  
  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }
`;

const slideInLeft = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-200px) scale(0.8);
    clip-path: polygon(0 0, 0 100%, 0 100%, 0 0);
  }
  50% {
    clip-path: polygon(0 0, 0 100%, 100% 100%, 100% 0);
  }
  100% {
    opacity: 1;
    transform: translateX(0) scale(1);
    clip-path: polygon(0 0, 0 100%, 100% 100%, 100% 0);
  }
  
  @media (max-width: 768px) {
    0% {
      transform: translateX(-100px) scale(0.9);
    }
  }
`;

const slideInRight = keyframes`
  0% {
    opacity: 0;
    transform: translateX(200px) scale(0.8);
    clip-path: polygon(100% 0, 100% 100%, 100% 100%, 100% 0);
  }
  50% {
    clip-path: polygon(0 0, 0 100%, 100% 100%, 100% 0);
  }
  100% {
    opacity: 1;
    transform: translateX(0) scale(1);
    clip-path: polygon(0 0, 0 100%, 100% 100%, 100% 0);
  }
  
  @media (max-width: 768px) {
    0% {
      transform: translateX(100px) scale(0.9);
    }
  }
`;

const MemberImage = styled.div`
  width: 100%;
  height: 400px;
  border-radius: 20px;
  overflow: hidden;
  margin-bottom: 2rem;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.4) 100%);
    pointer-events: none;
  }
  
  &:hover img {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    height: 300px;
    margin-bottom: 1.5rem;
  }
`;

const MemberSection = styled.div`
  margin-bottom: 6rem;
  opacity: 0;
  transform-origin: center;
  animation: ${props => props.index % 2 === 0 ? slideInLeft : slideInRight} 1.2s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  animation-delay: ${props => props.index * 0.4}s;
  perspective: 1000px;
  
  &:hover {
    .member-content {
      transform: translateY(-10px);
      box-shadow: 0 20px 50px rgba(0,0,0,0.1);
    }
  }
  
  @media (max-width: 768px) {
    margin-bottom: 3rem;
    animation-duration: 0.8s;
    animation-delay: ${props => props.index * 0.2}s;
  }
`;


const TabButton = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  background: ${props => props.active ? '#8B0000' : 'transparent'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 25px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.active ? '#8B0000' : '#f0f0f0'};
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin: 2rem 0;

  @media (max-width: 768px) {
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin: 1rem 0;
  }
`;

const MemberContent = styled.div`
  display: flex;
  flex-direction: column;
  background: white;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0,0,0,0.05);
  position: relative;

  @media (max-width: 768px) {
    padding: 1.5rem;
    text-align: center;

    h2 {
      font-size: 1.8rem;
      margin-bottom: 1rem;
    }

    p {
      font-size: 0.9rem;
      line-height: 1.6;
    }
  }
  
  h2 {
    font-size: 2.5rem;
    margin-bottom: 0.5rem;
    position: relative;
    display: inline-block;
    
    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: -10px;
      width: 50px;
      height: 4px;
      background: #8B0000;
    }
  }
  
  p {
    color: #666;
    line-height: 1.8;
    margin: 2rem 0;
  }

  @media (max-width: 1024px) {
    padding: 2rem;
  }
  
  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1.5rem;
  margin-top: 2rem;
  margin-bottom: 2rem;
  @media (max-width: 768px) {
    justify-content: center;
    margin-top: 1rem;
  }

  a {
    color: ${props => props.theme.colors.text};
    font-size: 1.4rem;
    transition: all 0.3s ease;
    padding: 0.5rem;
    border-radius: 50%;
    background: rgba(0,0,0,0.05);
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;

    &:hover {
      color: ${props => props.theme.colors.primary};
      transform: translateY(-3px) rotate(8deg);
      background: white;
      box-shadow: 0 5px 15px rgba(0,0,0,0.1);
    }
  }
`;


const teamMembers = [
  {
    id: 1,
    name: "Esther Howard",
    image: "/images/team/esther-howard.jpg",
    Mission: "Legal writing is essential to the practice of law, but it can be time-consuming and difficult. Frequently spends countless hours conducting legal research and analysis, drafting arguments, and revising their work",
    Vision: "To revolutionize legal writing through innovative technology and expert guidance, making it more efficient and accessible for legal professionals worldwide",
    OurValue: "Excellence, Innovation, and Client Success - we are committed to delivering the highest quality legal solutions while staying at the forefront of legal technology",
    additionalImages: [
      { src: "/images/team/esther-howard-2.jpg", caption: "Speaking at Legal Tech Conference" },
      { src: "/images/team/esther-howard-3.jpg", caption: "Leading team meeting" },
      { src: "/images/team/esther-howard-4.jpg", caption: "In the courtroom" }
    ],
    role: "Senior Legal Counsel",
    description: "With over 15 years of experience in corporate law, Esther specializes in complex litigation and regulatory compliance.",
    services: [
      {
        title: "Legal Advisory",
        description: "Expert guidance on corporate legal matters and regulatory compliance"
      },
      {
        title: "Risk Management",
        description: "Comprehensive risk assessment and mitigation strategies"
      },
      {
        title: "Contract Review",
        description: "Thorough analysis and drafting of legal documents and contracts"
      }
    ],
    social: {
      linkedin: "https://linkedin.com",
      twitter: "https://twitter.com",
      github: "https://github.com"
    }
  },
  {
    id: 2,
    name: "Jane Cooper",
    Mission: "Legal writing is essential to the practice of law, but it can be time-consuming and difficult. Frequently spends countless hours conducting legal research and analysis, drafting arguments, and revising their work",
    Vision: "To revolutionize legal writing through innovative technology and expert guidance, making it more efficient and accessible for legal professionals worldwide",
    OurValue: "Excellence, Innovation, and Client Success - we are committed to delivering the highest quality legal solutions while staying at the forefront of legal technology",

    image: "/images/team/jane-cooper.jpg",
    additionalImages: [
      { src: "/images/team/jane-cooper-2.jpg", caption: "Conducting legal research" },
      { src: "/images/team/jane-cooper-3.jpg", caption: "Client consultation" },
      { src: "/images/team/jane-cooper-4.jpg", caption: "Presenting findings" }
    ],
    role: "Legal Research Specialist",
    description: "Jane brings expertise in legal research and analysis, with a focus on precedent cases and regulatory frameworks.",
    services: [
      {
        title: "Legal Research",
        description: "In-depth research on case law and legal precedents"
      },
      {
        title: "International Law",
        description: "Expertise in cross-border legal matters and regulations"
      },
      {
        title: "Compliance Consulting",
        description: "Strategic advice on regulatory compliance and best practices"
      }
    ],
    social: {
      linkedin: "https://linkedin.com",
      twitter: "https://twitter.com",
      github: "https://github.com"
    }
  },
  {
    id: 3,
    name: "Robert Fox",
    Mission: "Legal writing is essential to the practice of law, but it can be time-consuming and difficult. Frequently spends countless hours conducting legal research and analysis, drafting arguments, and revising their work",
    Vision: "To revolutionize legal writing through innovative technology and expert guidance, making it more efficient and accessible for legal professionals worldwide",
    OurValue: "Excellence, Innovation, and Client Success - we are committed to delivering the highest quality legal solutions while staying at the forefront of legal technology",

    image: "/images/team/robert.jpg",
    role: "Legal Technology Expert",
    additionalImages: [
      { src: "/images/team/tyler-john-2.jpg", caption: "Case review session" },
      { src: "/images/team/tyler-john-3.jpg", caption: "Document preparation" },
      { src: "/images/team/tyler-john-4.jpg", caption: "Team collaboration" }
    ],

    description: "Robert combines legal expertise with technological innovation to streamline legal processes and enhance efficiency.",
    services: [
      {
        title: "Legal Tech Solutions",
        description: "Implementation of cutting-edge legal technology tools"
      },
      {
        title: "Process Optimization",
        description: "Streamlining legal workflows and documentation processes"
      },
      {
        title: "Document Automation",
        description: "Automated legal document generation and management"
      }
    ],
    social: {
      linkedin: "https://linkedin.com",
      twitter: "https://twitter.com",
      github: "https://github.com"
    }
  }
  ,
  {
    id: 4,
    name: "Tyler John",
    Mission: "Legal writing is essential to the practice of law, but it can be time-consuming and difficult. Frequently spends countless hours conducting legal research and analysis, drafting arguments, and revising their work",
    Vision: "To revolutionize legal writing through innovative technology and expert guidance, making it more efficient and accessible for legal professionals worldwide",
    OurValue: "Excellence, Innovation, and Client Success - we are committed to delivering the highest quality legal solutions while staying at the forefront of legal technology",
    role: "Legal Consultant",

    description: "Specialized in providing comprehensive legal research and analysis for complex cases.",
    services: [
      { title: "Legal Research", description: "In-depth research and case law analysis" },
      { title: "Case Strategy", description: "Development of comprehensive legal strategies" },
      { title: "Documentation", description: "Detailed legal documentation and reporting" }
    ],
    social: {
      linkedin: "#",
      twitter: "#",
      github: "#"
    }
  },
  {
    id: 5,
    name: "Sarah Parker",
    Mission: "Legal writing is essential to the practice of law, but it can be time-consuming and difficult. Frequently spends countless hours conducting legal research and analysis, drafting arguments, and revising their work",
    Vision: "To revolutionize legal writing through innovative technology and expert guidance, making it more efficient and accessible for legal professionals worldwide",
    OurValue: "Excellence, Innovation, and Client Success - we are committed to delivering the highest quality legal solutions while staying at the forefront of legal technology",
    image: "/images/team/sarah.jpg",
    role: "Legal Researcher",
    additionalImages: [
      { src: "/images/team/tyler-john-2.jpg", caption: "Case review session" },
      { src: "/images/team/tyler-john-3.jpg", caption: "Document preparation" },
      { src: "/images/team/tyler-john-4.jpg", caption: "Team collaboration" }
    ],

    description: "Expert in legal research methodologies and case law analysis with a focus on civil rights.",
    services: [
      { title: "Research Methods", description: "Advanced legal research methodologies" },
      { title: "Case Analysis", description: "Comprehensive case law review and analysis" },
      { title: "Civil Rights Focus", description: "Specialized research in civil rights cases" }
    ],
    social: {
      linkedin: "#",
      twitter: "#",
      github: "#"
    }
  }
];

const TeamMemberPage = () => {
  const [activeTab, setActiveTab] = React.useState('Mission');
  return (
    <PageWrapper>
      <BackgroundDesign />
      <SEO
        title="Our Team | The Big Heart Association Morocco"
        description="Meet the passionate team behind The Big Heart Association (TBH Morocco). Our dedicated board members and 70+ active volunteers drive education, health, and solidarity across Morocco."
        url="/team"
        breadcrumbs={[{ name: "Home", url: "/" }, { name: "Our Team", url: "/team" }]}
        noOrganizationSchema
      />
      <PageHero>
        <PageHeroContent>
          <PageTitle>Our <span>Team</span></PageTitle>
          <PageSubtitle>Meet our dedicated team of legal professionals who are committed to providing exceptional service and expertise in legal brief creation.</PageSubtitle>
        </PageHeroContent>
        
      </PageHero>
      <MainContent>
        
        <TeamSection>
          
          {teamMembers.map((member, index) => (
            <MemberSection key={member.id} index={index}>
              <MemberContent className="member-content" index={index}>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: window.innerWidth > 768 ? '1fr 1fr' : '1fr',
                  gap: window.innerWidth > 768 ? '4rem' : '2rem',
                  gridTemplateAreas: window.innerWidth > 768 ? "'content image'" : "'image' 'content'"
                }}>
                  <div style={{ gridArea: 'content' }}>
                    <h2>{member.name}</h2>
                    <p>{member.description}</p>
                    <TabContainer>
                      <TabButton 
                        active={activeTab === 'Mission'} 
                        onClick={() => setActiveTab('Mission')}
                      >
                        Mission
                      </TabButton>
                      <TabButton 
                        active={activeTab === 'Vision'} 
                        onClick={() => setActiveTab('Vision')}
                      >
                        Vision
                      </TabButton>
                      <TabButton 
                        active={activeTab === 'OurValue'} 
                        onClick={() => setActiveTab('OurValue')}
                      >
                        Our Value
                      </TabButton>
                    </TabContainer>
                    <p>{member[activeTab]}</p>

                    <SocialLinks>
                      <a href={member.social.linkedin} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faLinkedin} />
                      </a>
                      <a href={member.social.twitter} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faTwitter} />
                      </a>
                      <a href={member.social.github} target="_blank" rel="noopener noreferrer">
                        <FontAwesomeIcon icon={faGithub} />
                      </a>

                    </SocialLinks>
                    {member.additionalImages && member.additionalImages.length > 0 && (
                      <AdditionalImagesGrid>
                        {member.additionalImages.map((img, idx) => (
                          <AdditionalImage key={idx}>
                            <img src={img.src} alt={`${member.name} - ${img.caption}`} />
                            <div className="image-caption">{img.caption}</div>
                          </AdditionalImage>
                        ))}
                      </AdditionalImagesGrid>
                    )}
                  </div>
                  <div style={{ gridArea: 'image' }}>
                    <MemberImage>
                      <img src={member.image} alt={member.name} />
                    </MemberImage>
                    
                  </div>

                </div>

              </MemberContent>
            </MemberSection>
          ))}        
        </TeamSection>
      </MainContent>
    </PageWrapper>
  );

};

export default TeamMemberPage;
